import React, { useEffect, useState } from 'react';
import {
  BookOpen,
  Check,
  Edit3,
  FileText,
  Plus,
  Save,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { adminApi } from '../../services/adminApi';

const EMPTY_FORM = {
  slug: '',
  kind: 'ebook',
  title_en: '',
  title_es: '',
  title_it: '',
  desc_en: '',
  desc_es: '',
  desc_it: '',
  features_en: '',
  features_es: '',
  features_it: '',
  price_eur: '9.99',
  duration_minutes: 30,
  badge: '',
  is_active: true,
  sort_order: 10,
};

const productToForm = (p) => ({
  slug: p.slug || '',
  kind: p.kind || 'ebook',
  title_en: p.title_i18n?.en || '',
  title_es: p.title_i18n?.es || '',
  title_it: p.title_i18n?.it || '',
  desc_en: p.description_i18n?.en || '',
  desc_es: p.description_i18n?.es || '',
  desc_it: p.description_i18n?.it || '',
  features_en: (p.features_i18n?.en || []).join('\n'),
  features_es: (p.features_i18n?.es || []).join('\n'),
  features_it: (p.features_i18n?.it || []).join('\n'),
  price_eur: ((p.price_cents || 0) / 100).toFixed(2),
  duration_minutes: p.duration_minutes || 30,
  badge: p.badge || '',
  is_active: p.is_active !== false,
  sort_order: p.sort_order ?? 10,
});

const splitLines = (txt) =>
  (txt || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // 'new' | product.id | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [langTab, setLangTab] = useState('en');
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');

  const loadProducts = async () => {
    setLoading(true);
    try {
      const list = await adminApi.getProducts();
      setProducts(list);
    } catch (e) {
      setError(e.message || 'Could not load products.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const flash = (msg) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(''), 3500);
  };

  const startCreate = (kind = 'ebook') => {
    setError('');
    setEditingId('new');
    setForm({
      ...EMPTY_FORM,
      kind,
      price_eur: kind === 'consultation' ? '50.00' : '9.99',
    });
  };

  const startEdit = (product) => {
    setError('');
    setEditingId(product.id);
    setForm(productToForm(product));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const priceCents = Math.round(parseFloat(form.price_eur || '0') * 100);
      if (Number.isNaN(priceCents) || priceCents < 50) {
        throw new Error('Price must be at least €0.50');
      }
      const payload = {
        slug: form.slug.trim().toLowerCase().replace(/\s+/g, '_'),
        kind: form.kind,
        title_i18n: {
          en: form.title_en.trim() || form.title_es.trim(),
          es: form.title_es.trim() || form.title_en.trim(),
          it: form.title_it.trim() || form.title_en.trim(),
        },
        description_i18n: {
          en: form.desc_en.trim(),
          es: form.desc_es.trim() || form.desc_en.trim(),
          it: form.desc_it.trim() || form.desc_en.trim(),
        },
        features_i18n: {
          en: splitLines(form.features_en),
          es: splitLines(form.features_es).length ? splitLines(form.features_es) : splitLines(form.features_en),
          it: splitLines(form.features_it).length ? splitLines(form.features_it) : splitLines(form.features_en),
        },
        price_cents: priceCents,
        currency: 'eur',
        duration_minutes: form.kind === 'consultation' ? Number(form.duration_minutes) || 30 : null,
        badge: form.badge.trim() || null,
        is_active: Boolean(form.is_active),
        sort_order: Number(form.sort_order) || 0,
      };

      if (editingId === 'new') {
        await adminApi.createProduct(payload);
        flash('Product created!');
      } else {
        await adminApi.updateProduct(editingId, payload);
        flash('Changes saved!');
      }
      setEditingId(null);
      await loadProducts();
    } catch (err) {
      setError(err.message || 'Failed to save product.');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickPriceUpdate = async (product, newEurString) => {
    const cents = Math.round(parseFloat(newEurString) * 100);
    if (Number.isNaN(cents) || cents < 50) return;
    try {
      const updated = await adminApi.updateProduct(product.id, { price_cents: cents });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
      flash(`Updated price for "${product.title_i18n?.en || product.slug}" to €${(cents / 100).toFixed(2)}`);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePdfUpload = async (product, file) => {
    if (!file) return;
    setError('');
    setUploadingId(product.id);
    try {
      const updated = await adminApi.uploadProductPdf(product.id, file);
      setProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
      flash(`Uploaded PDF "${updated.pdf_filename}" for ${product.title_i18n?.en || product.slug}`);
    } catch (err) {
      setError(err.message || 'PDF upload failed.');
    } finally {
      setUploadingId(null);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.title_i18n?.en || product.slug}"?`)) return;
    try {
      await adminApi.deleteProduct(product.id);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      flash('Product deleted.');
    } catch (err) {
      setError(err.message);
    }
  };

  const consultations = products.filter((p) => p.kind === 'consultation');
  const ebooks = products.filter((p) => p.kind === 'ebook');

  return (
    <div className="space-y-8">
      {feedback && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          <Check className="w-4 h-4" />
          {feedback}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Consultation prices */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-blue-50 text-primary">
              <Video className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">1-on-1 Consultation Prices</h2>
              <p className="text-xs text-gray-500">
                Changes update the booking page and Stripe Checkout immediately.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {consultations.map((c) => (
            <div
              key={c.id}
              className="border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-4"
            >
              <div>
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">
                  {c.duration_minutes} minutes
                </span>
                <h3 className="font-bold text-gray-900">
                  {c.title_i18n?.en || c.slug}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                  {c.description_i18n?.en}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center border border-gray-300 rounded-lg px-2.5 py-1.5 bg-gray-50">
                  <span className="text-sm font-bold text-gray-500 mr-1">€</span>
                  <input
                    type="number"
                    step="0.50"
                    min="1"
                    defaultValue={(c.price_cents / 100).toFixed(2)}
                    onBlur={(e) => handleQuickPriceUpdate(c, e.target.value)}
                    className="w-20 bg-transparent text-right font-bold text-gray-900 focus:outline-none"
                    title="Edit price and click outside to save"
                  />
                </div>
                <button
                  onClick={() => startEdit(c)}
                  className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                  title="Edit details & translations"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Diet Ebooks / PDFs */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-50 text-emerald-600">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Diet Plans & eBooks (PDF Shop)</h2>
              <p className="text-xs text-gray-500">
                Upload PDF guides, set prices in EUR, and manage descriptions in English, Spanish, and Italian.
              </p>
            </div>
          </div>
          <button
            onClick={() => startCreate('ebook')}
            className="inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Diet eBook
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400 py-8 text-center">Loading products…</p>
        ) : (
          <div className="space-y-4">
            {ebooks.map((ebook) => (
              <div
                key={ebook.id}
                className="border border-gray-200 rounded-xl p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 hover:border-gray-300 transition-colors"
              >
                <div className="space-y-1 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900 text-base">
                      {ebook.title_i18n?.en || ebook.slug}
                    </span>
                    {ebook.badge && (
                      <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800">
                        {ebook.badge}
                      </span>
                    )}
                    {!ebook.is_active && (
                      <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        Hidden
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-0.5 rounded-full ${
                        ebook.has_pdf
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-amber-50 text-amber-700'
                      }`}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      {ebook.has_pdf
                        ? `${ebook.pdf_filename || 'PDF uploaded'} (${Math.round(
                            (ebook.pdf_size_bytes || 0) / 1024
                          )} KB)`
                        : 'No PDF uploaded yet'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{ebook.description_i18n?.en}</p>
                  <p className="text-xs text-gray-400">
                    ES: {ebook.title_i18n?.es || '—'} · IT: {ebook.title_i18n?.it || '—'}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  {/* Inline price input */}
                  <div className="flex items-center border border-gray-300 rounded-lg px-2.5 py-1.5 bg-gray-50">
                    <span className="text-sm font-bold text-gray-500 mr-1">€</span>
                    <input
                      type="number"
                      step="0.50"
                      min="0.50"
                      defaultValue={(ebook.price_cents / 100).toFixed(2)}
                      onBlur={(e) => handleQuickPriceUpdate(ebook, e.target.value)}
                      className="w-20 bg-transparent text-right font-bold text-gray-900 focus:outline-none"
                      title="Edit price and click outside to save"
                    />
                  </div>

                  {/* Upload PDF button */}
                  <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">
                    <Upload className="w-4 h-4" />
                    {uploadingId === ebook.id
                      ? 'Uploading…'
                      : ebook.has_pdf
                      ? 'Replace PDF'
                      : 'Upload PDF'}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      disabled={uploadingId === ebook.id}
                      onChange={(e) => handlePdfUpload(ebook, e.target.files?.[0])}
                    />
                  </label>

                  <button
                    onClick={() => startEdit(ebook)}
                    className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100"
                    title="Edit text, features & languages"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(ebook)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
                    title="Delete ebook"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Create / Edit modal */}
      {editingId && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <form
            onSubmit={handleSave}
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-2xl w-full p-6 sm:p-8 space-y-5 my-8"
          >
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {editingId === 'new' ? 'New Product / Diet eBook' : `Edit ${form.slug}`}
              </h3>
              <button
                type="button"
                onClick={() => setEditingId(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  Slug ID
                </label>
                <input
                  type="text"
                  required
                  disabled={editingId !== 'new'}
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="diet_mediterranean"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  Price (EUR €)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.50"
                  required
                  value={form.price_eur}
                  onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                  Badge (optional)
                </label>
                <input
                  type="text"
                  value={form.badge}
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  placeholder="Best Seller / New"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Language tabs */}
            <div>
              <div className="flex gap-2 border-b border-gray-200 mb-3">
                {[
                  { code: 'en', label: '🇬🇧 English' },
                  { code: 'es', label: '🇪🇸 Español' },
                  { code: 'it', label: '🇮🇹 Italiano' },
                ].map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setLangTab(l.code)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-t-lg border-b-2 transition-colors ${
                      langTab === l.code
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Title ({langTab.toUpperCase()})
                  </label>
                  <input
                    type="text"
                    value={form[`title_${langTab}`]}
                    onChange={(e) => setForm({ ...form, [`title_${langTab}`]: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    placeholder="1-Week Mediterranean Diet Plan"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Description ({langTab.toUpperCase()})
                  </label>
                  <textarea
                    rows={2}
                    value={form[`desc_${langTab}`]}
                    onChange={(e) => setForm({ ...form, [`desc_${langTab}`]: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                    Included features — one per line ({langTab.toUpperCase()})
                  </label>
                  <textarea
                    rows={3}
                    value={form[`features_${langTab}`]}
                    onChange={(e) => setForm({ ...form, [`features_${langTab}`]: e.target.value })}
                    placeholder={'7 daily meal plans with recipes\nComplete grocery list\nMeal prep guide'}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="rounded text-primary"
                />
                Visible on public website
              </label>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditingId(null)}
                  className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white text-sm font-semibold px-5 py-2 rounded-lg"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProductsManager;
